import React, { useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import { findSimilarRoomAssets } from '../packageOptions/roomNameMatch';
import type { RoomAssetMatch } from '../packageOptions/roomNameMatch';
import type { RoomAssetLink } from '../packageOptions/types';

interface Props {
  assets: Array<RoomAssetLink>;
  initialName: string;
  onClose: () => void;
  onCreate: (name: string) => void;
  onUseExisting: (asset: RoomAssetLink) => void;
  open: boolean;
}

// Mirrors the room "Create new asset" form, including its "is this the same one?" check.
export default function CreateInclusionAssetDialog({
  assets,
  initialName,
  onClose,
  onCreate,
  onUseExisting,
  open,
}: Props) {
  const [name, setName] = useState(initialName);
  const [similarMatches, setSimilarMatches] = useState<Array<RoomAssetMatch>>([]);
  const [selectedSimilarId, setSelectedSimilarId] = useState('');
  const trimmedName = name.trim();
  const exactMatch = similarMatches.some((match) => match.similarity === 1);

  useEffect(() => {
    if (open) {
      setName(initialName);
    }
  }, [initialName, open]);

  function handleCreate() {
    const matches = findSimilarRoomAssets(trimmedName, assets);
    if (matches.length > 0) {
      setSimilarMatches(matches);
      setSelectedSimilarId(matches[0].asset.id);
      return;
    }
    onCreate(trimmedName);
    onClose();
  }

  function handleUseExisting() {
    const match = similarMatches.find(({ asset }) => asset.id === selectedSimilarId);
    if (match) {
      onUseExisting(match.asset);
    }
    onClose();
  }

  function resetState() {
    setSimilarMatches([]);
    setSelectedSimilarId('');
  }

  if (similarMatches.length > 0) {
    return (
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={onClose}
        open={open}
        slotProps={{ transition: { onExited: resetState } }}
      >
        <DialogTitle>Is this the same asset?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You entered <strong>{trimmedName}</strong>. This {exactMatch ? 'is an exact match for' : 'looks similar to'}{' '}
            {similarMatches.length === 1 ? 'an asset' : 'assets'} that already{' '}
            {similarMatches.length === 1 ? 'exists' : 'exist'}.
          </Typography>
          <RadioGroup
            onChange={(event) => setSelectedSimilarId(event.target.value)}
            sx={{ flexWrap: 'nowrap', gap: 1, mt: 2 }}
            value={selectedSimilarId}
          >
            {similarMatches.map(({ asset }) => (
              <FormControlLabel
                control={<Radio />}
                key={asset.id}
                label={asset.name}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, m: 0, px: 1.5, py: 1 }}
                value={asset.id}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimilarMatches([])} variant="text">
            Back
          </Button>
          {/* See docs/business-rules.md#inclusion-assets - an exact duplicate is never created. */}
          {!exactMatch && (
            <Button
              onClick={() => {
                onCreate(trimmedName);
                onClose();
              }}
              variant="text"
            >
              No, create new
            </Button>
          )}
          <Button disabled={!selectedSimilarId} onClick={handleUseExisting} variant="contained">
            Yes, use existing
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open} slotProps={{ transition: { onExited: resetState } }}>
      <DialogTitle>Create new asset</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Name"
          onChange={(event) => setName(event.target.value)}
          sx={{ mt: 1 }}
          value={name}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button disabled={!trimmedName} onClick={handleCreate} variant="contained">
          Create asset
        </Button>
      </DialogActions>
    </Dialog>
  );
}
